/**
 * Brand Consultant Main Hook
 * 
 * Main hook for Brand Consultant functionality. Handles message sending,
 * credit validation, conversation management, and tool invocation processing.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth.tsx';
import { useCredits } from '@/hooks/useCredits';
import { Message, ChatResponse, BrandConsultantError, HIDDEN_TOOLS } from '../types';
import { toast } from '@/components/ui/use-toast';

const MESSAGE_COST = 5; // 5 credits per message as specified in requirements

export function useBrandConsultant(brandId?: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { session } = useAuth();
  const { balance, fetchBalance } = useCredits();

  // Load messages from session or initialize with welcome message
  const loadMessagesFromSession = useCallback(async (sessionId: string): Promise<void> => {
    if (!session) return;
    
    try {
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const response = await fetch(`${workerUrl}/rainfactory/brand-consultant/messages/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const sessionMessages = data.messages || [];
        
        if (sessionMessages.length > 0) {
          // Load previous conversation
          setMessages(sessionMessages);
        } else {
          // Start with welcome message if no previous conversation
          initializeWithWelcomeMessage();
        }
      } else {
        // If can't load messages, start fresh
        initializeWithWelcomeMessage();
      }
    } catch (err) {
      console.error('Error loading session messages:', err);
      initializeWithWelcomeMessage();
    }
  }, [session]);

  const initializeWithWelcomeMessage = useCallback(() => {
    setMessages([{
      id: crypto.randomUUID(),
      role: 'assistant',
      content: 'Hello! I\'m your Brand Consultant. I can help you with strategic insights, analyze your brand performance, review your content, and answer questions about your marketing efforts. What would you like to explore today?',
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const addMessage = useCallback((message: Message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const sendMessage = useCallback(async (
    content: string, 
    sessionId?: string
  ): Promise<boolean> => {
    if (!session) {
      setError('Authentication required');
      toast({
        title: 'Authentication Required',
        description: 'Please log in to use the Brand Consultant.',
        variant: 'destructive'
      });
      return false;
    }

    if (!brandId) {
      setError('Brand ID required');
      toast({
        title: 'Brand Required',
        description: 'Please select a brand to use the Brand Consultant.',
        variant: 'destructive'
      });
      return false;
    }

    // Check credits before sending
    if (!balance || balance.total_credits < MESSAGE_COST) {
      setError('Insufficient credits');
      toast({
        title: 'Insufficient Credits',
        description: `You need ${MESSAGE_COST} credits to send a message. Please purchase more credits.`,
        variant: 'destructive'
      });
      return false;
    }

    setIsLoading(true);
    setError(null);

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };

    addMessage(userMessage);

    try {
      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const response = await fetch(`${workerUrl}/rainfactory/brand-consultant/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          brandId,
          messages: [...messages, userMessage],
          sessionId
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to get response from Brand Consultant';
        try {
          const errorData = await response.json() as BrandConsultantError;
          errorMessage = errorData.error || errorMessage;
          
          if (errorData.isProviderOutage) {
            toast({
              title: 'Service Temporarily Unavailable',
              description: 'AI service is temporarily unavailable. Please try again later.',
              variant: 'destructive'
            });
            setError(errorMessage);
            return false;
          }
        } catch (e) {
          // If we can't parse the error, use the default message
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json() as ChatResponse;

      // Process tool invocations if present (filter out internal tools)
      if (data.toolInvocations && data.toolInvocations.length > 0) {
        // Filter out internal tools that shouldn't be visible to users
        const visibleInvocations = data.toolInvocations.filter(
          invocation => !HIDDEN_TOOLS.includes(invocation.toolName as any)
        );
        
        // Add tool invocation messages only for visible tools
        visibleInvocations.forEach(invocation => {
          const toolMessage: Message = {
            id: crypto.randomUUID(),
            role: 'tool',
            content: `Using tool: ${invocation.toolName}`,
            toolInvocations: [invocation],
            timestamp: invocation.timestamp
          };
          addMessage(toolMessage);
        });
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString(),
        creditsUsed: data.creditsUsed
      };

      addMessage(assistantMessage);

      // Refresh credit balance
      await fetchBalance();

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      console.error('Error sending message to Brand Consultant:', err);
      
      setError(errorMessage);
      
      // Add error message to chat
      const errorChatMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `⚠️ ${errorMessage}. Please try again or contact support if the issue persists.`,
        timestamp: new Date().toISOString()
      };
      
      addMessage(errorChatMessage);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [session, brandId, balance, messages, addMessage, fetchBalance]);

  const resetConversation = useCallback(() => {
    setMessages([{
      id: crypto.randomUUID(),
      role: 'assistant',
      content: 'Hello! I\'m your Brand Consultant. I can help you with strategic insights, analyze your brand performance, review your content, and answer questions about your marketing efforts. What would you like to explore today?',
      timestamp: new Date().toISOString()
    }]);
    setError(null);
  }, []);

  const hasAvailableCredits = balance ? balance.total_credits >= MESSAGE_COST : false;

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    addMessage,
    clearError,
    resetConversation,
    loadMessagesFromSession,
    initializeWithWelcomeMessage,
    hasAvailableCredits,
    messageCost: MESSAGE_COST,
    creditBalance: balance?.total_credits || 0
  };
}